import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import AdminCommandPalette, { useAdminCommandPalette } from "@/components/AdminCommandPalette";
import { useAuth } from "@/hooks/useAuth";
import { useBranding } from "@/hooks/useBranding";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import { useDeviceMode } from "@/hooks/useDeviceMode";
import AdminMobileAppShell from "@/components/layouts/AdminMobileAppShell";
import BrandLogo from "@/components/BrandLogo";
import {
  LayoutDashboard, LogOut, Menu, X, ChevronDown, ChevronRight, Search, Globe, Settings, ShieldCheck, PanelLeftOpen, PanelLeftClose,
} from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { NavLink } from "@/components/NavLink";
import { ThemeToggle } from "@/components/ThemeToggle";
import AdminNotificationBell from "@/components/AdminNotificationBell";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { adminSidebarGroups } from "@/config/adminSidebarConfig";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import EnvironmentBanner from "@/components/EnvironmentBanner";
import { useImpersonation } from "@/hooks/useImpersonation";
import { useNotificationListener } from "@/hooks/useNotificationListener";
import EnvironmentToggle from "@/components/admin/EnvironmentToggle";
import SoundUnlockPrompt from "@/components/SoundUnlockPrompt";
import { endTrackedAdminSession, useAdminSessionTracker } from "@/hooks/useAdminSessionTracker";


export default function AdminLayout() { // mobile-shell-v2
  useNotificationListener();
  const { user, signOut } = useAuth();
  const { appName } = useBranding();
  const { hasPermission, isSuperAdmin } = useAdminPermissions();
  const { isModuleEnabled, isLoading: modulesLoading } = useModuleSettings();
  const { isMobile } = useDeviceMode();
  const { isImpersonating } = useImpersonation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openParent, setOpenParent] = useState<string | null>(null);
  const cmdPalette = useAdminCommandPalette();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(adminSidebarGroups.map(g => g.key))
  );
  useAdminSessionTracker(!!user && !isImpersonating);

  // Filter children by permission + module, then filter parent items
  const filteredGroups = adminSidebarGroups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => {
          if (item.children) {
            const filteredChildren = item.children.filter((child) => {
              if (child.permission && !hasPermission(child.permission)) return false;
              if (child.moduleKey && !isModuleEnabled(child.moduleKey)) return false;
              return true;
            });
            return { ...item, children: filteredChildren };
          }
          return item;
        })
        .filter((item) => {
          if (item.children && item.children.length === 0) return false;
          if (item.permission && !hasPermission(item.permission)) return false;
          if (item.moduleKey && !isModuleEnabled(item.moduleKey)) return false;
          return true;
        }),
    }))
    .filter((group) => group.items.length > 0);

  // Strict route matchers: exact or nested under "/"
  const matchesRoute = (rawRoute: string) => {
    const route = rawRoute.split("?")[0];
    return location.pathname === route || location.pathname.startsWith(route + "/");
  };

  // Auto-expand the group + nested parent that contains the current route so the
  // menu always reflects where you are (including first load and refresh).
  useEffect(() => {
    for (const group of filteredGroups) {
      for (const item of group.items) {
        const hasChildren = item.children && item.children.length > 0;
        const isActive =
          matchesRoute(item.to) ||
          (hasChildren && item.children!.some((c) => matchesRoute(c.to)));
        if (!isActive) continue;
        setCollapsedGroups((prev) => {
          if (!prev.has(group.key)) return prev;
          const next = new Set(prev);
          next.delete(group.key);
          return next;
        });
        if (hasChildren) setOpenParent(item.to);
        return;
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile app shell — mirrors user portal pattern
  if (isMobile) {
    return (
      <>
        <AdminMobileAppShell />
        <AdminCommandPalette open={cmdPalette.open} onOpenChange={cmdPalette.setOpen} />
      </>
    );
  }

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const visibleKeys = filteredGroups.map((g) => g.key);
      if (prev.has(key)) {
        // Open this one, close all other visible groups (accordion).
        return new Set(visibleKeys.filter((k) => k !== key));
      }
      // Closing the group — also close any nested parent that lived inside it.
      const group = filteredGroups.find((g) => g.key === key);
      if (group && openParent && group.items.some((it) => it.to === openParent)) {
        setOpenParent(null);
      }
      return new Set([...prev, key]);
    });
  };

  const toggleParent = (parentTo: string) => {
    setOpenParent((prev) => (prev === parentTo ? null : parentTo));
  };

  const handleSignOut = async () => {
    await endTrackedAdminSession("Staff signed out");
    await signOut();
    navigate("/admin/login", { replace: true });
  };

  const userEmail = user?.email || "admin";
  const userInitials = userEmail.substring(0, 2).toUpperCase();

  const renderSidebarNav = (onItemClick?: () => void) => {
    const isExpanded = onItemClick || !collapsed;

    const dashboardActive = location.pathname === "/admin/dashboard" || location.pathname.startsWith("/admin/dashboard/");

    return (
      <nav className="flex-1 py-1 px-2 overflow-y-auto">
        {/* Standalone Dashboard link */}
        {!isExpanded ? (
          <div className="mb-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/admin/dashboard"
                  className={cn(
                    "flex justify-center py-2.5 rounded-lg transition-colors",
                    dashboardActive ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                  )}
                >
                  <LayoutDashboard className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Dashboard</TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <NavLink
            to="/admin/dashboard"
            end
            className={cn(
              "flex items-center gap-2.5 px-3 py-1.5 mb-1 rounded-lg text-[13px] font-medium min-w-0 transition-colors",
              dashboardActive
                ? "bg-primary/10 text-primary font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
            )}
            onClick={onItemClick}
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" />
            <span className="whitespace-normal break-words leading-snug text-left min-w-0">Dashboard</span>
          </NavLink>
        )}

        {modulesLoading && (
          <div className="space-y-3 px-1 pt-2" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-muted/40 animate-pulse" />
                <div className="h-8 w-full rounded-lg bg-muted/40 animate-pulse" />
                <div className="h-8 w-full rounded-lg bg-muted/40 animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {!modulesLoading && filteredGroups.map((group) => {
          const isOpen = !collapsedGroups.has(group.key);

          // Collapsed mode: just icons
          if (!isExpanded) {
            const isGroupActive = group.items.some((item) =>
              location.pathname === item.to || location.pathname.startsWith(item.to + "/") ||
              (item.children && item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/")))
            );
            return (
              <div key={group.key} className="mb-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={group.items[0]?.to || "#"}
                      className={cn(
                        "flex justify-center py-2.5 rounded-lg transition-colors",
                        isGroupActive ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                      )}
                    >
                      <group.icon className="h-5 w-5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">{group.label}</TooltipContent>
                </Tooltip>
              </div>
            );
          }

          // Expanded mode: collapsible groups
          return (
            <Collapsible
              key={group.key}
              open={isOpen}
              onOpenChange={() => toggleGroup(group.key)}
              className="mb-1"
            >
              <CollapsibleTrigger className="flex items-center justify-between gap-2 w-full text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold px-3 pt-3.5 pb-1 hover:text-foreground transition-colors cursor-pointer select-none">
                <span className="flex items-center gap-1.5 min-w-0">
                  <group.icon className="h-3 w-3 shrink-0" />
                  <span className="whitespace-normal break-words leading-snug text-left min-w-0">{group.label}</span>
                </span>
                <ChevronRight className={`h-3 w-3 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
              </CollapsibleTrigger>
               <CollapsibleContent>
                <div className="relative ml-[14px] py-1 space-y-0.5">
                  {group.items.map((item, itemIdx) => {
                    const hasChildren = item.children && item.children.length > 0;
                    const isParentActive = location.pathname === item.to ||
                      location.pathname.startsWith(item.to + "/") ||
                      (hasChildren && item.children!.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/")));
                    const isParentExpanded = openParent === item.to;
                    const isLastItem = itemIdx === group.items.length - 1;

                    if (hasChildren) {
                      return (
                        <div key={item.to} className="space-y-0.5">
                          <div
                            className={cn(
                              "relative flex items-center rounded-xl pl-6 pr-3 py-1.5 text-[13px] font-medium transition-all cursor-pointer",
                              "before:absolute before:left-0 before:top-0 before:h-1/2 before:w-4 before:border-l before:border-b before:border-border/40",
                                      isParentActive
                                        ? "bg-primary/10 text-primary ml-1 rounded-lg"
                                        : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground",
                                      !isLastItem && "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-border/40"
                            )}
                            >
                              {isParentActive && (
                                <span aria-hidden className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-primary" />
                              )}
                            <Link
                              to={item.to}
                              className="flex items-center gap-2.5 flex-1 min-w-0"
                              onClick={() => { setOpenParent(item.to); onItemClick?.(); }}
                            >
                              <item.icon className="h-4 w-4 shrink-0" />
                              <span className="whitespace-normal break-words leading-snug text-left min-w-0" title={item.label}>{item.label}</span>
                            </Link>
                            <button
                              type="button"
                              aria-label={`${isParentExpanded ? "Collapse" : "Expand"} ${item.label}`}
                              aria-expanded={isParentExpanded}
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleParent(item.to); }}
                              className="p-2 -mr-1 rounded-md hover:bg-accent/60 transition-colors"
                            >
                             <ChevronRight className={cn("h-3.5 w-3.5 transition-transform duration-200", isParentExpanded && "rotate-90")} />
                            </button>
                          </div>

                          {isParentExpanded && (
                            <div className="relative ml-[18px] py-1">
                              {item.children!.map((child, idx) => {
                                const [childPath, childQuery] = child.to.split("?");
                                const childTab = childQuery ? new URLSearchParams(childQuery).get("tab") : null;
                                const currentTab = new URLSearchParams(location.search).get("tab");
                                const pathMatch = location.pathname === childPath || location.pathname.startsWith(childPath + "/");
                                const siblingUsesTabs = item.children!.some((c) => c.to.startsWith(childPath + "?"));
                                const childActive = pathMatch && (!siblingUsesTabs || childTab === currentTab);
                                const isLast = idx === item.children!.length - 1;
                                return (
                                  <Link
                                    key={child.to}
                                    to={child.to}
                                    onClick={onItemClick}
                                    className={cn(
                                      "relative flex items-center pl-5 pr-3 py-1 text-xs font-normal rounded-lg transition-all",
                                      "before:absolute before:left-0 before:top-0 before:h-1/2 before:w-4 before:border-l before:border-b before:border-border/40",
                                      childActive
                                        ? "text-primary font-medium bg-primary/10 ml-1"
                                        : "text-muted-foreground hover:text-foreground hover:bg-accent/40",
                                      !isLast && "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-border/40"
                                    )}
                                    title={child.label}
                                  >
                                    <span title={child.label} className="whitespace-normal break-words leading-snug text-left min-w-0">{child.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Flat item with tree connector
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={cn(
                          "relative flex items-center gap-2.5 pl-6 pr-3 py-1.5 rounded-xl text-[13px] font-medium min-w-0 text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground transition-all",
                          "before:absolute before:left-0 before:top-0 before:h-1/2 before:w-4 before:border-l before:border-b before:border-border/40",
                          !isLastItem && "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-border/40"
                        )}
                        activeClassName="bg-primary/10 text-primary font-semibold ml-1 rounded-lg"
                        onClick={onItemClick}
                      >
                        {(location.pathname === item.to || location.pathname.startsWith(item.to + "/")) && (
                          <span aria-hidden className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-primary" />
                        )}
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="whitespace-normal break-words leading-snug text-left min-w-0" title={item.label}>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </nav>
    );
  };

  const profileCard = (showFull: boolean) => (
    <div className={`border-t border-border/50 p-3 ${!showFull ? "flex justify-center" : ""}`}>
      {showFull ? (
        <div className="flex items-center gap-3 px-2">
          <Avatar className="h-9 w-9 border border-primary/30">
            <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">{userInitials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate text-foreground">{userEmail.split("@")[0]}</p>
            <p className="text-[11px] text-muted-foreground truncate">{userEmail}</p>
            <p className="text-[10px] text-muted-foreground/70 truncate" title="App version">v{__APP_BUILD__}</p>
          </div>
          <span className="text-[10px] font-semibold text-primary px-1.5 py-0.5 rounded bg-primary/10 shrink-0">
            {isSuperAdmin ? "Super Admin" : "Staff"}
          </span>
        </div>
      ) : (
        <Avatar className="h-8 w-8 border border-primary/30">
          <AvatarFallback className="bg-primary/20 text-primary text-[10px] font-bold">{userInitials}</AvatarFallback>
        </Avatar>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col glass-strong transition-all duration-300 fixed left-0 z-30 ${collapsed ? "w-16" : "w-[272px]"}`}
        style={{ top: "var(--env-banner-height, 0px)", height: "calc(100vh - var(--env-banner-height, 0px))" }}
      >
        <div className={`flex items-center h-16 px-4 border-b border-border/50 ${collapsed ? "justify-center" : "justify-between"}`}>
          {!collapsed && (
            <Link to="/admin/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <BrandLogo className="h-8 max-w-[160px] object-contain" />
            </Link>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>
        <TooltipProvider delayDuration={300}>
          {renderSidebarNav()}
        </TooltipProvider>
        {profileCard(!collapsed)}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[288px] glass-strong flex flex-col">
            <div className="flex items-center justify-between h-16 px-4 border-b border-border/50">
              <Link to="/admin/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <BrandLogo className="h-8 max-w-[160px] object-contain" />
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <TooltipProvider delayDuration={300}>
              {renderSidebarNav(() => setMobileOpen(false))}
            </TooltipProvider>
            {profileCard(true)}
          </div>
          <div className="flex-1 bg-background/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main Content — single scroll owner is the document, not this container */}
      <div
        className={`flex-1 flex flex-col min-w-0 overflow-x-hidden transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-[272px]"}`}
        style={{ minHeight: "calc(100vh - var(--env-banner-height, 0px))", marginTop: "var(--env-banner-height, 0px)" }}
      >
        <header className="sticky top-0 z-40 glass-strong border-b border-border/50 h-16 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/admin/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity lg:hidden">
              <BrandLogo className="h-7 max-w-[130px] object-contain" />
              
            </Link>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-foreground h-9 px-3 rounded-lg bg-muted/30 border border-border/40"
              onClick={() => cmdPalette.setOpen(true)}
            >
              <Search className="h-3.5 w-3.5" />
              <span className="text-xs">Search…</span>
              <kbd className="ml-2 hidden md:inline-flex items-center rounded border border-border/50 bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                ⌘K
              </kbd>
            </Button>
            <Button variant="ghost" size="icon" className="sm:hidden" onClick={() => cmdPalette.setOpen(true)}>
              <Search className="h-4 w-4" />
            </Button>

            <EnvironmentToggle />
            <AdminNotificationBell />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <Avatar className="h-8 w-8 border border-primary/30">
                    <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">{userInitials}</AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/admin/my-settings")}>
                  <Settings className="h-4 w-4 mr-2" /> My Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/admin/my-security")}>
                  <ShieldCheck className="h-4 w-4 mr-2" /> Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/")}>
                  <Globe className="h-4 w-4 mr-2" /> View Site
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <EnvironmentBanner />
        <ImpersonationBanner />
        <main className="flex-1 p-4 lg:p-8 content-glow-bg relative z-10">
          <Outlet />
        </main>
      </div>

      {/* Command Palette */}
      <AdminCommandPalette open={cmdPalette.open} onOpenChange={cmdPalette.setOpen} />
      <SoundUnlockPrompt />

    </div>
  );
}
